import { EFlags } from '@feathr/blackbox';

export interface IFlagMetadata {
  label: string;
  description: string;
}

export function flagMetadata(flag: EFlags): IFlagMetadata {
  const metadata: Record<EFlags, IFlagMetadata> = {
    [EFlags.aiGroupBuilder]: {
      label: 'Show AI Group Builder',
      description: 'Reveals the AI group builder in the segment filter editor.',
    },
    [EFlags.bypassMX]: {
      label: 'Bypass MX',
      description: 'Skips MX record verification when sending email from custom domains.',
    },
    [EFlags.creditCardConvenienceFeeAccepted]: {
      label: 'Credit Card Convenience Fee Accepted',
      description:
        'Suppresses the credit card surcharge disclosure once the account has acknowledged it.',
    },
    [EFlags.csmManager]: {
      label: 'CSM Manager',
      description: 'Renames CSM labels to "Account Manager" across the UI.',
    },
    [EFlags.enforceOnboarding]: {
      label: 'Enforce Onboarding',
      description: 'Blocks access to the main app until onboarding is completed.',
    },
    [EFlags.hasBlackbaudRaisersEdge]: {
      label: 'Has Blackbaud Raisers Edge',
      description: "Enables the Blackbaud Raiser's Edge NXT integration on the integrations page.",
    },
    [EFlags.hasGoogleAds]: {
      label: 'Has Google Ads',
      description: 'Enables the Google Ads integration on the integrations page.',
    },
    [EFlags.hasImis]: {
      label: 'Has IMIS',
      description: 'Enables the iMIS CRM integration on the integrations page.',
    },
    [EFlags.hasMultiConversions]: {
      label: 'Has Multi Conversions',
      description: 'Allows campaigns to track multiple conversion events instead of a single one.',
    },
    [EFlags.locales]: {
      label: 'Locales',
      description: 'Reveals the language / locale selector in the user profile.',
    },
    [EFlags.mailrail]: {
      label: 'Mailrail',
      description: 'Use Mailrail for email campaigns',
    },
    [EFlags.metaIntegrationOutage]: {
      label: 'Meta Integration Outage',
      description: 'Disables the Meta integration UI to indicate an active service outage.',
    },
    [EFlags.minBudget]: {
      label: 'Min Budget',
      description: 'Enforces the account-level minimum campaign budget.',
    },
    [EFlags.monetization]: {
      label: 'Monetization',
      description: 'Enables revenue and ROI tracking on budget cards and reports.',
    },
    [EFlags.noMaxBudget]: {
      label: 'No Max Budget',
      description: 'Removes the upper cap on campaign budgets.',
    },
    [EFlags.overridePackageImplementationsAccess]: {
      label: 'Override Implementations Access',
      description: "Grants implementation access regardless of the account's license tier.",
    },
    [EFlags.pinpointSendAllEmails]: {
      label: 'Pinpoint Send All Emails',
      description:
        'Adds the "send to all emails" option when configuring Pinpoint email audiences.',
    },
    [EFlags.pinpoint]: {
      label: 'Pinpoint',
      description: 'Routes outgoing email through Amazon Pinpoint instead of the default sender.',
    },
    [EFlags.showAccountReports]: {
      label: 'Show Account Reports',
      description: 'Adds the account-level (cross-campaign) reports section to the nav.',
    },
    [EFlags.showAllAffinityAudiences]: {
      label: 'Show All Affinity Audiences',
      description: 'Removes category restrictions on affinity audiences in the ad wizard.',
    },
    [EFlags.showAPIKeysV2]: {
      label: 'Show API Keys V2',
      description: 'Switches the API keys settings page to the v2 scoped service-key UI.',
    },
    [EFlags.showCampaignRecommendations]: {
      label: 'Show Campaign Recommendations',
      description: 'Enables optimization recommendations on TTD ad campaigns.',
    },
    [EFlags.showCommandPalette]: {
      label: 'Show Command Palette',
      description: 'Enables the ⌘K command palette for quick navigation and search.',
    },
    [EFlags.showConversionsSettingsV2]: {
      label: 'Show Conversions Settings V2',
      description: 'Switches the conversions settings page to the v2 attribution-window UI.',
    },
    [EFlags.showDripBulkEnroll]: {
      label: 'Show Drip Bulk Enroll',
      description: 'Enables bulk enrollment of contacts into drip campaigns.',
    },
    [EFlags.showDripCampaigns]: {
      label: 'Show Drip Campaigns',
      description: 'Enables drip / automation campaign creation and management.',
    },
    [EFlags.showEmailActivityExport]: {
      label: 'Show Email Activity Export',
      description: 'Adds the export option to email activity reports.',
    },
    [EFlags.showForms]: {
      label: 'Show Forms',
      description: 'Enables the Forms feature (form creation, embedding, and management).',
    },
    [EFlags.showFundraiseCRM]: {
      label: 'Show Fundraising CRM',
      description: 'Enables CRM integration settings within Fundraising campaigns.',
    },
    [EFlags.showFundraise]: {
      label: 'Show Fundraising',
      description: 'Enables Fundraising campaign features in the nav and main UI.',
    },
    [EFlags.showGoalsV2]: {
      label: 'Show Goals 2.0',
      description: 'Switches goal creation and management to the v2 UI.',
    },
    [EFlags.showGrowthCredits]: {
      label: 'Show Growth Credits',
      description: 'Shows the growth credits card at the bottom of the primary nav.',
    },
    [EFlags.showNewPrimaryNav]: {
      label: 'Show New Primary Nav',
      description: 'Switches to the redesigned primary navigation layout.',
    },
    [EFlags.showOnboarding]: {
      label: 'Show Onboarding',
      description:
        'Reveals the onboarding flow in the nav (pair with Enforce Onboarding to require it).',
    },
    [EFlags.showProResources]: {
      label: 'Show Pro Resources',
      description:
        'Reveals the Pro Resources page and unlocks implementation access for non-premium packages.',
    },
    [EFlags.showUsageMetrics]: {
      label: 'Show Usage Metrics',
      description: 'Shows billing usage metrics on the License page.',
    },
    [EFlags.showUsersAndRolesV2]: {
      label: 'Show Users and Roles V2',
      description: 'Switches users and roles management to the v2 UI.',
    },
    [EFlags.useContactLimits]: {
      label: 'Use Contact Limits',
      description: 'Enforces contact / audience size limits on segments and campaigns.',
    },
    [EFlags.useEmailLimits]: {
      label: 'Use Email Limits',
      description: 'Enforces email sending limits and shows the monthly / per-campaign breakdown.',
    },
    [EFlags.zapier]: {
      label: 'Zapier',
      description: 'Enables the Zapier integration card on the integrations page.',
    },
  };
  return metadata[flag];
}
